import { Component, Output, OnInit, EventEmitter, Input, input, signal, computed } from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import { UserDTO } from 'src/app/DTO/UserDTO';
import {CustomFieldDTO} from "../../../../../DTO/CustomFieldDTO";
import {DateTime} from "luxon";
import {CommunityService} from "../../../../../services/community/community.service";
import {CommunityDTO} from "../../../../../DTO/CommunityDTO";
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input-gg';
import { first, map } from 'rxjs';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { CustomFieldsConstants } from 'src/app/constants/CustomFieldsConstants';

@Component({
  selector: 'app-user-data',
  templateUrl: './user-data.component.html',
  styleUrls: ['./user-data.component.scss'],
})
export class UserDataComponent implements OnInit {

  @Input() fullFree: boolean;
  @Input() customFields: CustomFieldDTO[];
  @Input() submitAttempt = new EventEmitter();

  public rgpd = input<string>();

  @Output() userEmitter = new EventEmitter();  

  public form: FormGroup;
  public formLoaded = signal(false);
  public formFields = signal<CustomFieldDTO[]>([]);
  public includePhoneField = computed(() => !this.customFields.some(x => x.fieldLocation === 'PARENT'));
  public submitAttempted = false;
  public community: CommunityDTO = new CommunityDTO();
  public formattedPhone: any;
  public SearchCountryField = SearchCountryField;
  public CountryISO = CountryISO;
  public PhoneNumberFormat = PhoneNumberFormat;
  public preferredCountries: CountryISO[] = [CountryISO.Spain];
  public defaultCountry: any;
  public phone: string
  public showPhone = false;
  public fieldTypes = CustomFieldsConstants.InputTypes;
  public termsAccepted = signal(false);

  public onChange: (_: string) => void = (_: string) => { };

  private selectedCountry: string;
  private readonly baseFieldKeys = new Set(['NAME', 'SURNAME', 'MAIL', 'PHONE']);

  constructor(private _formBuilder: FormBuilder,
              private communityService: CommunityService,) {
  }

  ngOnInit(): void {
    this.setPhoneDefaultCountry()
    this.setForm();
    this.submitAttempt.subscribe((x => this.submitAttempted = true));
    this.communityService.Get().subscribe(res=> {
      this.community = res;
    });
  }

  public onRgpdChange(checked: boolean) {    
    this.form.get('rgpdAccepted').setValue(
      checked 
      ? DateTime.now() 
      : null);      
  }

  private setPhoneDefaultCountry() {
    this.communityService.GetParameters()
      .pipe(
        first(),
        map(x => {
          if (x['PHONE_DEFAULT_COUNTRY_CODE']) {
            const defaultCode = x['PHONE_DEFAULT_COUNTRY_CODE'];
            const enumValues = Object.values(CountryISO);

            if (enumValues.includes(defaultCode)) {
              this.defaultCountry = defaultCode as CountryISO[keyof CountryISO]
            }

            if (!this.preferredCountries.includes(defaultCode)) {
              this.preferredCountries.push(defaultCode);
            }
          } else {
            this.defaultCountry = this.preferredCountries[0];
          }

          this.showPhone = true;
        })
      ).subscribe()
  }

  private setForm() {
    const mailReg = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

    const noWhitespaceValidator = (control: FormControl) => {
      const isWhitespace = (control.value || '').toString().trim().length === 0;
      return !isWhitespace ? null : { whitespace: true };
    };

    const nameValidators = [Validators.required, noWhitespaceValidator];
    const surnameValidators = [Validators.required, noWhitespaceValidator];
    const mailValidators = [Validators.required, Validators.pattern(mailReg), noWhitespaceValidator];

    this.form = new FormGroup({
      name: new FormControl('', nameValidators),
      surname: new FormControl('', surnameValidators),
      mail: new FormControl('', mailValidators),            
      acceptance: new FormControl(false, null),      
      rgpdAccepted: new FormControl(null, this.rgpd() ? Validators.required : null)
    })    

    if (this.includePhoneField()) {
      this.form.addControl('phone', new FormControl('', null));
    }
    
    this.formFields.set(this.customFields.filter(field => {
      if (!field?.key) {
        // Backward compatibility for legacy custom fields without key.
        return true;
      }

      return !this.baseFieldKeys.has(field.key.toUpperCase());
    }));
    
    for (const customField of this.formFields()) {
      this.form.addControl(
        customField.id.toString(),
        this._formBuilder.control('', customField.required ? [Validators.required, noWhitespaceValidator] : null)
      );
    }

    this.formLoaded.set(true);

    this.form.valueChanges.subscribe((x)=> {
      var user = new UserDTO();
      user.name = this.form.get('name').value;
      user.mail = this.form.get('mail').value;
      user.surname = this.form.get('surname').value;

      if (this.includePhoneField()) {
        user.phone = this.form.get('phone').value;
      }

      user.rgpdAccepted = this.form.get('rgpdAccepted').value;
      var acceptance =  this.form.get('acceptance').value;

      if (acceptance) {
        user.acceptanceCommercial = DateTime.now().toJSDate();
      } else {
        user.acceptanceCommercial = null;
      }

      const customFieldValues: Record<number, unknown> = {};
      for (const field of this.formFields()) {
        const value = this.form.get(field.id.toString())?.value;
        customFieldValues[field.id] = value != null && value !== '' ? value : null;
      }

      this.userEmitter.emit({
        'user-data': user,
        formValid: this.form.valid,
        customFieldValues
      })
    })
  }
}
